// src/pages/markets/board/board/BoardColumn.tsx

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Virtuoso } from 'react-virtuoso';
import { ColumnHeader } from './ColumnHeader';
import { PhoneColumnHeader } from '../phone/PhoneColumnHeader';
import { useSortFreeze } from './useSortFreeze';
import { useMouseHover } from './useMouseHover';
import {
    type BodyMode,
    type CardView,
    type ColumnConfig,
} from './columnConfig';
import { useColumnEvents } from '../api/useColumnEvents';
import { useSeries } from '../api/useSeries';
import { useActivity } from '../api/useActivity';
import { useNewArrivals } from '../api/useNewArrivals';
import { columnDefaults, columnFilterKeys, scoreGroups as schemaScoreGroups, sortOptions } from '../api/useSchema';
import { boardKeys } from '../api/boardKeys';
import { getEventBatcher } from '../api/eventBatcher';
import {
    GATED_FIELDS,
    type BoardFilters,
    type BoardSchema,
    type ScreenerEvent,
} from '../api/boardTypes';
import { applyMuted } from '../state/mutedTopics';
import { useMutedTopics } from '../state/useMutedTopics';
import type { ActivityContext, ActivityWindows } from '../card/ActivityBlock';
import { ScoredEventCard } from './ScoredEventCard';
import { CardSkeleton } from '../card/CardSkeleton';
import { CARD_HEIGHT, ROW_HEIGHT } from '../card/cardLayout';
import { isSingleMarketEvent } from '../api/scoreIndex';
import { hoverScrollbar } from '../../../../styles/hoverScrollbar';
import type { CategoryConfig } from './categories';
import { eventHref } from './routes';
import { openInNewTab } from '../../../../utils/newTab';

export interface BoardColumnProps {
    config: ColumnConfig;
    schema: BoardSchema;
    filters: BoardFilters;
    defaults: BoardFilters;
    bodyMode: BodyMode;
    cardView: CardView;
    now: number;
    onFiltersChange: (patch: Partial<BoardFilters>) => void;
    onReset: () => void;
    onShare: () => string;
    onBodyModeChange: (mode: BodyMode) => void;
    onCardViewChange: (view: CardView) => void;
    onCategoryChange: (category: CategoryConfig) => void;
    /** Phone board: the control row and options sheet instead of the desktop header. */
    phone?: boolean;
}

const VISIBLE_BLOCK = 10;
const CARD_SHADOW_GAP = 6;
// The phone mockup puts 8 px between cards.
const PHONE_CARD_GAP = 8;
const FOCUS_STALE_MS = 5 * 60_000;
const FOCUS_GC_MS = 10 * 60_000;

interface ListContext {
    loadingMore: boolean;
}

function ListFooter({ context }: { context?: ListContext }) {
    return context?.loadingMore ? <CardSkeleton /> : null;
}

const LIST_COMPONENTS = { Footer: ListFooter };

function Placeholder({ text, action }: { text: string; action?: React.ReactNode }) {
    return (
        <Box sx={{ px: 1.25, py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{text}</Typography>
            {action}
        </Box>
    );
}

export function BoardColumn({
    config,
    schema,
    filters,
    defaults,
    bodyMode,
    cardView,
    now,
    onFiltersChange,
    onReset,
    onShare,
    onBodyModeChange,
    onCardViewChange,
    onCategoryChange,
    phone = false,
}: BoardColumnProps) {
    const Header = phone ? PhoneColumnHeader : ColumnHeader;
    const cardGap = phone ? PHONE_CARD_GAP : CARD_SHADOW_GAP;
    const qc = useQueryClient();
    const [atTop, setAtTop] = useState(true);
    const { hovered, handlers: hoverHandlers } = useMouseHover();
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: VISIBLE_BLOCK });
    const [viewOverrides, setViewOverrides] = useState<Set<string>>(() => new Set());
    const [focusEventId, setFocusEventId] = useState<string | null>(null);

    const scoreGroups = useMemo(() => {
        const set = new Set(schemaScoreGroups(schema));
        if (filters.group) set.add(filters.group);
        return [...set];
    }, [schema, filters.group]);

    const filterKeys = useMemo(
        () => columnFilterKeys(schema, config.source),
        [schema, config.source],
    );

    // Muted topics ride along as extra exclusions on every request, and nowhere else.
    const { effective: muted } = useMutedTopics();
    // The group comes from the resolved category, not from live filter state: a schema
    // refetch can drop the group a mounted pane is on, and the two must not disagree
    // on the wire. They are equal in every other state.
    const wireFilters = useMemo(
        () => ({ ...applyMuted(filters, muted), group: config.group }),
        [filters, muted, config.group],
    );

    const list = useColumnEvents({
        column: config.source,
        filters: wireFilters,
        filterKeys,
        scoreGroups,
        enabled: config.available,
    });

    // Hover holds the order as hard as being scrolled down does: activity
    // ranking churns every tick, and a list that moves under the cursor costs
    // the reader the click they were about to make.
    const frozen = useSortFreeze(list.events, !atTop || hovered);
    const filterKey = useMemo(() => JSON.stringify(wireFilters), [wireFilters]);
    const arrivals = useNewArrivals(list.rankIds, filterKey, {
        ready: list.isSettled,
        pageCount: list.pageCount,
    });

    useEffect(() => {
        setFocusEventId(null);
    }, [filterKey]);

    const focusQuery = useQuery<ScreenerEvent, Error>({
        queryKey: boardKeys.event(focusEventId ?? ''),
        queryFn: () => getEventBatcher(qc).load(focusEventId as string),
        enabled: Boolean(focusEventId),
        staleTime: FOCUS_STALE_MS,
        gcTime: FOCUS_GC_MS,
    });

    const focusEvent = focusEventId ? (focusQuery.data ?? null) : null;

    const listEvents = useMemo(() => {
        if (!focusEvent) return frozen.events;
        return [
            focusEvent,
            ...frozen.events.filter(e => e.event_id !== focusEvent.event_id),
        ];
    }, [focusEvent, frozen.events]);

    // Rounded to blocks of ten: without it every scroll pixel makes a new query
    // key, and the overlay refetches instead of hitting the cache.
    const visibleEvents = useMemo(() => {
        const start = Math.max(0, Math.floor(visibleRange.start / VISIBLE_BLOCK) * VISIBLE_BLOCK);
        const end = Math.ceil((visibleRange.end + 1) / VISIBLE_BLOCK) * VISIBLE_BLOCK;
        return listEvents.slice(start, end);
    }, [listEvents, visibleRange]);

    // The group a card shows scores for. A discovery pane carries none of its
    // own, so it falls back to the baseline column's — read off the schema
    // rather than named here. Empty only before the schema arrives, which is
    // also before there is a card to render.
    const defaultGroup = filters.group ?? columnDefaults(schema, 'all_traders').group ?? '';

    // One batch for the markets the cards start on. A card stepped somewhere
    // else fetches its own series, which is the rarer case.
    const seriesMarketIds = useMemo(() => {
        if (bodyMode !== 'trend' && config.bodyVariant === 'meta') return [];
        const ids: string[] = [];
        for (const event of visibleEvents) {
            // Trend mode wants every card's line; otherwise only the
            // single-market cards draw one, so only they go in the batch. The
            // New column draws none, so it pays for none.
            if (bodyMode !== 'trend' && !isSingleMarketEvent(event)) continue;
            const primary = event.primary_market_id ?? event.markets[0]?.market_id;
            if (primary) ids.push(primary);
        }
        return [...new Set(ids)];
    }, [bodyMode, config.bodyVariant, visibleEvents]);

    const series = useSeries({
        marketIds: seriesMarketIds,
        window: config.seriesWindow,
        enabled: config.available && seriesMarketIds.length > 0,
    });

    const isActivityColumn = config.bodyVariant === 'activity';

    const activityIds = useMemo(
        () => (isActivityColumn ? visibleEvents.map(e => e.event_id) : []),
        [isActivityColumn, visibleEvents],
    );

    const activity = useActivity({
        eventIds: activityIds,
        filters,
        filterKeys,
        enabled: config.available && isActivityColumn,
    });

    // From the filter state rather than the response: the panel and the card
    // must name the same window even on the tick where a switch has not landed.
    const activityWindows = useMemo<ActivityWindows>(
        () => ({
            drift: filters.driftWindow ?? '24h',
            jump: filters.jumpWindow ?? '15m',
        }),
        [filters.driftWindow, filters.jumpWindow],
    );

    const activityFor = useCallback(
        (eventId: string): ActivityContext | null => {
            if (!isActivityColumn) return null;
            return {
                activity: activity.byEvent.get(eventId) ?? null,
                unit: activity.unit,
                metricMode: filters.metricMode ?? 'absolute',
                windows: activityWindows,
            };
        },
        [
            isActivityColumn,
            activity.byEvent,
            activity.unit,
            filters.metricMode,
            activityWindows,
        ],
    );

    const filtersDirty = useMemo(
        () =>
            filters.sortBy !== defaults.sortBy ||
            filters.sortDir !== defaults.sortDir ||
            filters.tagMatch !== defaults.tagMatch ||
            JSON.stringify(filters.eventsInclude) !== JSON.stringify(defaults.eventsInclude) ||
            JSON.stringify(filters.eventsExclude) !== JSON.stringify(defaults.eventsExclude) ||
            JSON.stringify(filters.tagsInclude) !== JSON.stringify(defaults.tagsInclude) ||
            JSON.stringify(filters.tagsExclude) !== JSON.stringify(defaults.tagsExclude) ||
            GATED_FIELDS.some(f => filters[f] !== defaults[f]),
        [filters, defaults],
    );

    const openEvent = useCallback(
        (eventId: string, slug?: string | null) =>
            openInNewTab(eventHref(eventId, slug)),
        [],
    );

    const toggleCard = useCallback((eventId: string) => {
        setViewOverrides(prev => {
            const next = new Set(prev);
            if (next.has(eventId)) next.delete(eventId);
            else next.add(eventId);
            return next;
        });
    }, []);

    const handleCardViewChange = useCallback(
        (view: CardView) => {
            setViewOverrides(new Set());
            onCardViewChange(view);
        },
        [onCardViewChange],
    );

    const rangeChanged = useCallback(
        (range: { startIndex: number; endIndex: number }) =>
            setVisibleRange(prev =>
                prev.start === range.startIndex && prev.end === range.endIndex
                    ? prev
                    : { start: range.startIndex, end: range.endIndex },
            ),
        [],
    );

    const hasData = list.events.length > 0;

    let content: React.ReactNode;

    if (!config.available) {
        content = <Placeholder text={config.emptyText} />;
    } else if (list.isLoading) {
        content = (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${cardGap}px` }}>
                {Array.from({ length: 4 }).map((_, i) => (
                    <CardSkeleton key={i} />
                ))}
            </Box>
        );
    } else if (list.error && !hasData) {
        content = (
            <Placeholder
                text="This column did not load."
                action={
                    <Button size="small" onClick={list.retry} sx={{ mt: 1, fontSize: phone ? '13px' : '11px', minHeight: phone ? 44 : undefined, textTransform: 'none' }}>
                        Try again
                    </Button>
                }
            />
        );
    } else if (listEvents.length === 0) {
        content = <Placeholder text={config.emptyText} />;
    } else {
        content = (
            <Virtuoso
                style={{ height: '100%' }}
                data={listEvents}
                defaultItemHeight={
                    (cardView === 'row' ? ROW_HEIGHT : CARD_HEIGHT) + cardGap
                }
                atTopStateChange={setAtTop}
                rangeChanged={rangeChanged}
                endReached={list.fetchNextPage}
                increaseViewportBy={{ top: 0, bottom: CARD_HEIGHT * 2 }}
                context={{ loadingMore: list.isFetchingNextPage }}
                components={LIST_COMPONENTS}
                // BoardColumn.tsx — itemContent, index is now used
                itemContent={(index, event) => {
                    // The lead event carries the column: full card, everything below is a row.
                    const baseCollapsed = index === 0 ? false : cardView === 'row';
                    return (
                        <Box sx={{ pb: `${cardGap}px` }}>
                            <ScoredEventCard
                                event={event}
                                scoreGroups={scoreGroups}

                                seriesByMarket={series.byMarket}
                                seriesFetching={series.isFetching}
                                seriesCovered={seriesMarketIds}
                                bodyVariant={config.bodyVariant}
                                showGroupToggle={config.showGroupToggle}
                                defaultGroup={defaultGroup}
                                cardMetrics={config.cardMetrics}
                                bodyMode={bodyMode}
                                trendWindow={config.seriesWindow}
                                now={now}
                                activityCtx={activityFor(event.event_id)}
                                collapsed={baseCollapsed !== viewOverrides.has(event.event_id)}
                                activeTags={filters.tagsInclude}
                                isNew={arrivals.isNew(event.event_id)}
                                onSeen={arrivals.markSeen}
                                onToggleCollapsed={toggleCard}
                                onOpen={openEvent}
                            />
                        </Box>
                    );
                }}
            />
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                minWidth: 0,
                height: '100%',
                boxShadow: 1,
                borderRadius: '4px',
                overflow: 'hidden',
                bgcolor: 'background.paper',
                // The phone board draws its cards straight on the page.
                ...(phone && { boxShadow: 'none', borderRadius: 0, bgcolor: 'transparent' }),
            }}
        >
            <Header
                config={config}
                schema={schema}
                filters={filters}
                sortKeys={sortOptions(schema, config.source)}
                bodyMode={bodyMode}
                cardView={cardView}
                eventCount={frozen.events.length}
                pendingCount={frozen.pendingCount}
                isFetching={list.isFetching || series.isFetching || activity.isFetching}
                filtersDirty={filtersDirty}
                lastError={list.error}
                lastErrorAt={list.errorUpdatedAt}
                now={now}
                onFiltersChange={onFiltersChange}
                onReset={onReset}
                onShare={onShare}
                onBodyModeChange={onBodyModeChange}
                onCardViewChange={handleCardViewChange}
                onApplyPending={frozen.apply}
                onRetry={list.retry}
                onFocusEvent={setFocusEventId}
                onCategoryChange={onCategoryChange}
            />

            {focusEventId && (
                <Box
                    sx={{
                        px: 1,
                        py: 0.5,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Chip
                        label={focusQuery.isError ? 'Focus did not load' : 'Clear focus'}
                        size="small"
                        onDelete={() => setFocusEventId(null)}
                        onClick={() => setFocusEventId(null)}
                        sx={phone
                            ? { height: 44, fontSize: '13px', borderRadius: '22px' }
                            : { height: 22, fontSize: '11px', borderRadius: '4px' }}
                    />
                    {focusQuery.isLoading && (
                        <Typography sx={{ fontSize: phone ? '12px' : '11px', color: 'text.secondary' }}>
                            Loading pinned event…
                        </Typography>
                    )}
                </Box>
            )}

            <Box
                {...hoverHandlers}
                sx={{ flex: 1, minHeight: 0, px: phone ? 1.5 : 0.75, pt: phone ? 0 : 0.75, ...hoverScrollbar }}
            >
                {content}
            </Box>
        </Box>
    );
}